import React, { useState, useEffect } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import axios from "axios";
import logo from "../../Admin/images/PPKM_Logo.png"; // Import your logo image

// Define the types for the data
interface EventBlastItem {
  email: string | null;
  eventID: string;
  event_name: string;
  formatted_datetime_created: string;
  invitation_userID: string | null;
  member_userID: string;
  name: string | null;
  userID: string | null;
}

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "Open Sans",
  },
  row: {
    flexDirection: "row",
    justifyContent: "flex-start", // Align items to the left
    alignItems: "center", // Center items vertically
    marginBottom: 10, // Space between rows
  },
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#000",
    marginBottom: 10,
    borderCollapse: "collapse", // Collapse borders to look like an Excel table
  },
  rowTable: {
    flexDirection: "row", // Aligns cells horizontally within each row
    borderBottomWidth: 1, // Add a border between each row
    borderBottomColor: "#000",
  },
  headerCell: {
    padding: 6, // Reduced padding for a more compact look
    fontSize: 10, // Reduced font size for headers
    borderRightWidth: 1,
    borderRightColor: "#000",
    fontWeight: "bold",
    textAlign: "center", // Center align text for headers
    backgroundColor: "#f0f0f0", // Light gray background for headers
  },
  cellTable: {
    padding: 6, // Reduced padding for a more compact look
    fontSize: 9, // Reduced font size for data cells
    borderRightWidth: 1,
    borderColor: "#000",
    textAlign: "center", // Center align text
  },
  noColumn: {
    width: "5%", // Narrower width for the "No." column
  },
  otherColumn: {
    width: "31.6%", // Equal width for other columns (3 columns, 100% / 3 = ~33.3%)
  },
});

type BlastedReportProps = {
  eventID: string;
  date: string | null;
};

const BlastedReport: React.FC<BlastedReportProps> = ({ eventID, date }) => {
  const [eventBlastedData, setEventBlastedData] = useState<
    EventBlastItem[] | null
  >(null); // Changed the type to an array of EventBlastItem
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (eventID && date) {
      const fetchData = async () => {
        const payload = { eventID: eventID, date: date };
        // console.log(payload);
        try {
          setLoading(true);
          const response = await axios.post(
            "https://www.ppkm.org.my/api_server/get_event_blast_report_by_date/",
            // "http://localhost/api_server/get_event_blast_report_by_date/",
            payload
          );

          console.log("API Response:", response.data);

          // Check if response.data is an array and handle null values
          if (Array.isArray(response.data)) {
            const sanitizedData = response.data.map((item) => ({
              email: item.email || "N/A", // Replace null with a default value
              eventID: item.eventID,
              event_name: item.event_name || "No Name Provided",
              formatted_datetime_created:
                item.formatted_datetime_created || "Unknown Date",
              invitation_userID: item.invitation_userID || "N/A",
              member_userID: item.member_userID,
              name: item.name || "Anonymous",
              userID: item.userID || "N/A",
            }));

            setEventBlastedData(sanitizedData);
          } else {
            console.error("Unexpected response format:", response);
            setEventBlastedData([]);
          }
        } catch (err) {
          console.error("Error fetching data:", err);
          setError("Error fetching data.");
          setEventBlastedData([]);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [eventID && date]);

  if (loading) {
    return <Text>Loading...</Text>;
  }

  if (error) {
    return <Text>Error: {error}</Text>;
  }

  if (!eventBlastedData || eventBlastedData.length === 0) {
    return <Text>No data available.</Text>;
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.row}>
          <Image src={logo} style={{ width: "250px", height: "auto" }} />
        </View>

        <View style={styles.table}>
          {/* Header row */}
          <View style={styles.rowTable}>
            <Text style={[styles.headerCell, styles.noColumn]}>No</Text>
            <Text style={[styles.headerCell, styles.otherColumn]}>Name</Text>
            <Text style={[styles.headerCell, styles.otherColumn]}>Email</Text>
            <Text style={[styles.headerCell, styles.otherColumn]}>
              Date Blast
            </Text>
          </View>

          {/* Data rows */}
          {eventBlastedData.map((item, index) => (
            <View key={index} style={styles.rowTable}>
              <Text style={[styles.cellTable, styles.noColumn]}>
                {index + 1}
              </Text>
              {/* No column */}
              <Text style={[styles.cellTable, styles.otherColumn]}>
                {item.name || "No name available"}
              </Text>
              <Text style={[styles.cellTable, styles.otherColumn]}>
                {item.email || "No email provided"}
              </Text>
              <Text style={[styles.cellTable, styles.otherColumn]}>
                {item.formatted_datetime_created || "No date provided"}
              </Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default BlastedReport;
